import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ShieldCheck } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";

export default function Login() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const authError = searchParams.get("error");

  useEffect(() => {
    if (user) navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  const startMicrosoftLogin = () => {
    setLoading(true);
    // Relative, same-origin path — must stay on whichever host/domain the
    // user is actually browsing (preview, default live domain, or a custom
    // domain) so the OAuth session cookie set here is sent back by the
    // browser when Microsoft redirects to AZURE_AD_REDIRECT_URI. Using the
    // absolute REACT_APP_BACKEND_URL here previously sent the user to a
    // DIFFERENT host than the registered Azure redirect URI, so the
    // session cookie never made it back (session_expired loop).
    window.location.assign("/api/auth/azure/login");
  };

  return (
    <main className="min-h-screen flex items-center justify-center p-4 bg-[#0A838A]" data-testid="microsoft-login-page">
      <section
        className="w-full max-w-[280px] rounded-[11px] bg-white px-6 py-6 text-center shadow-[0_20px_42px_rgba(0,0,0,0.2)]"
        data-testid="microsoft-login-card"
      >
        <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-lg bg-[#EFF9F9] text-[#0A838A]">
          <ShieldCheck size={19} strokeWidth={2.5} />
        </div>
        <h1 className="mt-4 text-[16px] font-heading font-bold text-slate-900">Supplier Finance Hub</h1>
        <p className="mt-1.5 text-[11px] leading-relaxed text-slate-500">Sign in with your organization account to continue</p>
        {authError && (
          <p className="mt-3 text-[11px] font-bold text-red-600" data-testid="microsoft-login-error">
            Microsoft sign-in could not be completed. Please try again.
          </p>
        )}
        <Button
          onClick={startMicrosoftLogin}
          disabled={loading}
          data-testid="microsoft-sign-in-button"
          className="mt-4 w-full gap-2 bg-[#0A838A] hover:bg-[#076D74] text-white text-[11px] font-bold h-8 shadow-none"
        >
          {loading ? <span className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin" /> : <MicrosoftLogo />}
          {loading ? "Opening Microsoft…" : "Continue with Microsoft"}
        </Button>
        <p className="mt-5 text-[9px] leading-relaxed text-slate-400">Access is managed by your IT administrator. Contact them if you’re unable to sign in.</p>
      </section>
    </main>
  );
}

function MicrosoftLogo() {
  return (
    <svg width="13" height="13" viewBox="0 0 21 21" fill="none" aria-hidden="true">
      <rect width="10" height="10" fill="#F25022" />
      <rect x="11" width="10" height="10" fill="#7FBA00" />
      <rect y="11" width="10" height="10" fill="#00A4EF" />
      <rect x="11" y="11" width="10" height="10" fill="#FFB900" />
    </svg>
  );
}